/* Created by Interactive 63 - https://interactive63.com */
/**
 * Plain, serialisable data types describing an SVG Tweener project, plus the editor-only state
 * that lives outside it. Times are integer milliseconds and percentages run from 0 to 100.
 */

import type { Easing } from '../easing/types';

export type { Easing };

/** Stable identifier assigned to each element on import, stored as data-st-id. */
export type ElementId = string; // "e1", "e2", ...

export type PropertyKey =
  | 'translate' // { x, y } user units
  | 'rotate' // number, degrees
  | 'scale' // { x, y } multiplier, linked by default
  | 'skew' // { x, y } degrees
  | 'opacity' // number 0..1
  | 'fill' // colour string
  | 'stroke' // colour string
  | 'strokeWidth' // number, user units
  | 'fillOpacity' // number 0..1
  | 'strokeOpacity' // number 0..1
  | 'draw' // { start, end } percent of path length visible (0..100)
  | 'blur' // number, user units
  | 'stopColor' // colour string, <stop> only
  | 'stopOpacity' // number 0..1, <stop> only
  | 'morph' // { d: string } path data in the element's own coordinates
  | 'fontSize' // number, user units, <text> and <tspan> only
  | 'words'; // string, the text's content (one line per row), <text> and <tspan> only

export interface Vec2 {
  x: number;
  y: number;
}
export interface DrawValue {
  start: number;
  end: number;
}
export interface MorphValue {
  /** Path data in the element's own coordinates. */
  d: string;
  /** Manual start point alignment against the previous keyframe's shape. */
  align?: { shift: number; reverse: boolean };
  /**
   * Points of the previous keyframe's shape and the points of this shape they become, each counted
   * across the shape's parts in size order, largest first.
   */
  pins?: [from: number, to: number][];
  /** For each part of this shape, in size order, the part of the previous shape it grows from. */
  parts?: (number | null)[];
  /** Where a shape taken from another layer was placed: at that layer, or fitted to this one. */
  placement?: 'world' | 'in-place';
  /** The layer the shape was taken from, so its placement can be changed later. */
  sourceId?: ElementId;
}
export type PropertyValue = number | string | Vec2 | DrawValue | MorphValue;

/**
 * Handles that bend a position track's path through a keyframe, as offsets from the keyframe's
 * position in translate units. `in` shapes the path arriving, `out` the path leaving.
 */
export interface MotionHandles {
  in?: Vec2;
  out?: Vec2;
  /** The handles move independently instead of staying in a straight line. */
  corner?: boolean;
}

export interface Keyframe {
  id: string;
  time: number; // ms from scene start, 0..scene.duration
  value: PropertyValue;
  easing: Easing; // easing of the segment from this keyframe to the next
  motion?: MotionHandles; // position keyframes only: a curved path through this keyframe
}

/** Repeats a track's keyframes as a cycle inside the scene. */
export interface TrackRepeat {
  /** Cycle length in ms. null = automatic: the time of the track's last keyframe. */
  period: number | null;
  /** Number of cycles (2 to 1000), or 'infinite' to fill the scene. */
  count: number | 'infinite';
  /** Odd-numbered cycles play backwards (ping-pong). */
  alternate: boolean;
}

export interface Track {
  id: string;
  elementId: ElementId;
  property: PropertyKey;
  keyframes: Keyframe[]; // always kept sorted by time, unique times
  enabled: boolean; // disabled tracks are ignored by the compiler
  repeat?: TrackRepeat; // optional: repeat the keyframes as a cycle inside the scene
  /** false = play during the first scene iteration only. Absent or true = restart with the scene. */
  restartWithScene?: boolean;
  /** Position tracks only: the element turns to face the way it is travelling. */
  orient?: boolean;
}

export interface ElementMeta {
  id: ElementId;
  tag: string; // lower-case tag name, e.g. "path", "g", "stop"
  label: string; // user-facing name
  parentId: ElementId | null;
  childIds: ElementId[];
  origin: Vec2; // transform origin, percent of fill-box, default { x: 50, y: 50 }
  excludeFromExport: boolean;
  hiddenInEditor: boolean; // editor-only visibility toggle, never exported
  pathLength?: number; // cached total length for geometry elements, used by "draw"
}

export type Trigger =
  | { type: 'auto' }
  | { type: 'hover'; mode: 'play-reverse' | 'restart' | 'pause-resume' }
  | { type: 'click'; mode: 'toggle' | 'restart' | 'play-once' }
  | { type: 'inView'; threshold: number; once: boolean; onLeave: 'pause' | 'reset' | 'continue' }
  | { type: 'scroll'; startOffset: number; endOffset: number; smoothing: number };

export interface SceneSettings {
  duration: number; // ms, 100..120000
  delay: number; // ms
  iterations: number | 'infinite';
  direction: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
  fillMode: 'none' | 'forwards' | 'backwards' | 'both';
  trigger: Trigger;
  reducedMotion: 'final-frame' | 'first-frame' | 'ignore';
}

/** Options for the GIF and MP4 exports, which render the animation to frames. */
export interface MediaSettings {
  width: number; // output width in pixels; the height follows the artwork's proportions
  fps: number;
  loop: boolean; // GIF only: repeat forever instead of playing once
  plays: number; // MP4 only: how many times the animation runs in the video
}

export interface ExportSettings {
  codeType: 'css' | 'css-js';
  format: 'html-document' | 'embed-snippet' | 'animated-svg' | 'zip' | 'gif' | 'mp4';
  media: MediaSettings;
  runtimeDelivery: 'inline' | 'external-file';
  sceneName: string; // used for the scene class, e.g. "logo" -> .st-logo
  prefixIds: boolean;
  minify: boolean;
  responsive: boolean; // width 100%, height auto
  accessibleTitle: string; // empty = decorative (aria-hidden)
}

export interface Project {
  schemaVersion: 1;
  id: string;
  name: string;
  createdAt: string; // ISO
  updatedAt: string; // ISO
  svg: {
    source: string; // sanitised, prepared markup including data-st-id attributes
    viewBox: { x: number; y: number; width: number; height: number };
    fileName: string;
    originalWidth?: string; // root width attribute before sizing was removed
    originalHeight?: string;
    background?: string; // "#rrggbb" fill behind the artwork; absent means transparent
  };
  elements: Record<ElementId, ElementMeta>;
  rootIds: ElementId[]; // top-level children of the <svg>
  tracks: Track[];
  scene: SceneSettings;
  export: ExportSettings;
}

/** Editor-only state: never saved into project files and never recorded in undo history. */
export interface EditorState {
  selection: { elementIds: ElementId[]; keyframeIds: string[] };
  playhead: number; // ms
  isPlaying: boolean;
  stageZoom: number;
  stagePan: Vec2;
  timelineZoom: number; // px per second
  timelineScroll: number;
  autoKey: boolean; // editing a value on a keyed track writes a keyframe
  snapping: boolean;
  expandedElementIds: ElementId[];
  hoveredElementId: ElementId | null; // element under the pointer in the stage or layers
}

export const GEOMETRY_TAGS = ['path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon'];
export const CONTAINER_TAGS = ['g', 'svg', 'a', 'symbol'];

/** Narrows an unknown value to a plain object with numeric x and y. */
export function isVec2(v: unknown): v is Vec2 {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Partial<Vec2>).x === 'number' &&
    typeof (v as Partial<Vec2>).y === 'number'
  );
}

/** Narrows an unknown value to a draw range with numeric start and end. */
export function isDrawValue(v: unknown): v is DrawValue {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Partial<DrawValue>).start === 'number' &&
    typeof (v as Partial<DrawValue>).end === 'number'
  );
}

/** Narrows an unknown value to a morph target holding path data. */
export function isMorphValue(v: unknown): v is MorphValue {
  return typeof v === 'object' && v !== null && typeof (v as Partial<MorphValue>).d === 'string';
}
